import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
//mui
import { Card, TextField, Autocomplete, Typography, Container, LinearProgress, Checkbox, Box, Table, TableRow, TableHead, TableBody, TableCell, FormHelperText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';

const useStyles = makeStyles(() => ({
    foodCard: {
        maxHeight: '100% !important',
        borderRadius: '1.7rem !important',
        margin: '0.8rem !important',
    },
    statmentAutoComplite: {
        '&.MuiAutocomplete-root .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
            width: "0 !important",
            minWidth: "0px !important"
        }
    },
    cardTitleAndIcon: {
        marginRight: '0.5rem !important',
        marginTop: '0.3rem !important',
        alignItems: 'center !important',
    },
    cardTitle: {
        margin: '0.5rem !important',
        fontSize: '0.9rem !important',
        alignItems: 'center !important',
        fontWeight: "bold !important"
    },
    tc: {
        "&.MuiTableCell-body": {
            padding: '0 !important'
        }
    }
}));

const NO_FOODPREF = "01";


const FoodPreference = ({
    household,
    foodPreferencesList,
    cardCode,
    foodListApi,
    foodValidator,
    setFoodValidator,
    clearSignature
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [foodList, setFoodList] = useState([]);
    const [updateFoodPref, setUpdateFoodPref] = useState(false);
    const childFoodPrefs = [];

    // Initialize food preference list
    useEffect(() => {
        if (foodPreferencesList.length > 0 && !!foodListApi) {
            foodPreferencesList.forEach((food) => {
                const foodFound = foodListApi.find((foodPref) => food.FoodPrefCode === foodPref.FoodCode);
                if (foodFound) {
                    childFoodPrefs.push({ FoodCode: food.FoodPrefCode, FoodName: foodFound.FoodName });
                }
            });
            setFoodList(childFoodPrefs);
        }
    }, [foodListApi]);

    // Insert to household on every change
    useEffect(() => {
        clearSignature();
        const validator = foodList.length && updateFoodPref;
        setFoodValidator(validator);
        if (!!foodList.length) {
            insertToHousehold();
        }
    }, [foodList, updateFoodPref]);

    // Code list creation to update child food preferences
    const insertToHousehold = () => {
        const child = household.ChildrenList?.results.find((child) => child.CardCode === cardCode);

        if (foodList.every(({ FoodCode }) => FoodCode !== NO_FOODPREF)) {
            const codeList = foodList.map((food) => ({ FoodPrefCode: food.FoodCode }));
            child.FoodPreferencesList.results = codeList;
        } else {
            child.FoodPreferencesList.results = [{ FoodPrefCode: NO_FOODPREF }];
        }
    };

    const disabledOptions = (option) => {
        const isChosen = foodList.every(({ FoodCode }) => FoodCode !== NO_FOODPREF);
        const isFound = foodList.find(({ FoodCode }) => FoodCode === option.FoodCode);

        if (option.FoodCode === NO_FOODPREF) {
            return foodList.length > 0 && isChosen;
        } else {
            return !isChosen || isFound;
        }
    };

    const names = foodList.map((di) => di.FoodName);

    return (
        <>
            <Card className={classes.foodCard} data-html2canvas-ignore="true">
                {!!foodListApi ? (
                    <Container>
                        <Box display="flex" alignItems="center" className={classes.cardTitleAndIcon}>
                            <RestaurantIcon sx={{ color: "#3a7735" }} />
                            <Typography className={classes.cardTitle}>{t('foodPreference')} </Typography>
                        </Box>
                        <Box flex={1} display='flex' flexDirection='column' alignItems="flex-start" padding='1rem'>
                            <Autocomplete
                                defaultValue={childFoodPrefs}
                                fullWidth
                                limitTags={3}
                                disableClearable
                                getOptionDisabled={disabledOptions}
                                multiple
                                options={foodListApi.sort((a, b) => {
                                    if (a.FoodName === t("otherCompareString")) return 1;
                                    if (b.FoodName === t("otherCompareString")) return -1;
                                    if (a.FoodName === t("noFoodCompareString")) return -1;
                                    if (b.FoodName === t("noFoodCompareString")) return 1;
                                    return a.FoodName.toString().localeCompare(b.FoodName.toString());
                                })}
                                renderOption={(props, food, { selected }) => (
                                    <li {...props} key={food.FoodCode}>
                                        <Checkbox
                                            icon={<CheckBoxOutlineBlankOutlinedIcon sx={{ color: '#3a7735' }} />}
                                            checkedIcon={<CheckBoxOutlinedIcon sx={{ color: '#3a7735' }} />}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        <Typography>{food.FoodName}</Typography>
                                    </li>
                                )}
                                isOptionEqualToValue={(option, value) => option.FoodCode === value.FoodCode}
                                className={classes.statmentAutoComplite}
                                noOptionsText={t('notOption')}
                                getOptionLabel={(option) => option.FoodName || ""}
                                onChange={(event, value, reason) => {
                                    setFoodList(value);
                                    if (reason === 'selectOption') {
                                        setUpdateFoodPref(true);
                                    }
                                }}
                                style={{ minWidth: '10rem' }}
                                renderInput={(params) => (
                                    <TextField {...params} variant='standard' label={t('choose')} />
                                )}
                            />


                            <Box display="flex" alignItems="center" sx={{ mt: "0.3rem" }}>
                                <Checkbox
                                    icon={<CheckBoxOutlineBlankOutlinedIcon sx={{ color: '#3a7735' }} />}
                                    checkedIcon={<CheckBoxOutlinedIcon sx={{ color: '#3a7735' }} />}
                                    onChange={(e) => setUpdateFoodPref(e.target.checked)}
                                    checked={updateFoodPref}
                                />
                                <Typography variant='greenSmallText'>{t('foodPrefNote')}</Typography>
                            </Box>
                            {/* error message */}
                            {!foodValidator && (
                                <FormHelperText error style={{ fontSize: "0.78rem" }}>
                                    {`${t('foodError')} ${t('foodError1')}`}
                                </FormHelperText>
                            )}
                        </Box>
                    </Container>
                ) : (
                    <LinearProgress color="primary" />
                )}
            </Card>

            {/* Printable section */}
            <Box justifyContent='center' className={`${"showOnPrint"}_${cardCode}`} sx={{ display: "none" }}>
                <Box display="flex" alignItems="center" className={classes.cardTitleAndIcon}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontSize: "1rem", fontWeight: "bold" }}>
                                    {`${t('foodPreference')} `}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                {foodList.every((el) => el === NO_FOODPREF) ? (
                                    <TableCell className={classes.tc}>{t('noFoodPref')}</TableCell>
                                ) : (
                                    <TableCell className={classes.tc}>{names.join(", ")}</TableCell>
                                )}
                            </TableRow>
                            <TableRow>
                                <Typography variant='greenText'>V {t('foodPrefNote')}</Typography>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Box>
            </Box>
        </>
    );
};

export default FoodPreference;